import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { analytics } from "./client";
import { selectSession } from "../session/selectors";
import { useConsent } from "./ConsentContext";
import { subFromToken } from "./identity";

/**
 * Bridges the Redux auth session to analytics identity. Renders nothing.
 * Only identifies once consent is granted (ConsentProvider drives the gate).
 *  - on login (with consent) → identify (Cognito sub) + identifyTenant (workspace)
 *  - user_logged_in fires only on an actual login (distinctId absent → present),
 *    NOT when consent is granted mid-session
 *  - on logout → user_logged_out + reset
 */
export default function AnalyticsBridge() {
  const session = useSelector(selectSession());
  const { decision } = useConsent();
  const identified = useRef(false);
  const prevDistinctId = useRef(undefined);

  useEffect(() => {
    const distinctId = session?.user?.sub || subFromToken(session?.token);
    // session.tenant is the active workspace id (a string, used as X-Tenant-Id).
    const tenant = session?.tenant;

    // A real login is distinctId transitioning absent → present — not consent being
    // granted later (which leaves distinctId unchanged).
    const justLoggedIn = !prevDistinctId.current && Boolean(distinctId);
    prevDistinctId.current = distinctId;

    if (distinctId && decision === "granted") {
      if (!identified.current) {
        analytics.identify(distinctId);
        if (tenant) {
          analytics.identifyTenant(tenant);
        }
        identified.current = true;
      }
      if (justLoggedIn) {
        // Auth method isn't on the session yet; omit it rather than mis-attributing.
        analytics.track("user_logged_in", {});
      }
    } else if (!distinctId && identified.current) {
      analytics.track("user_logged_out", {});
      analytics.reset();
      identified.current = false;
    }
  }, [session, decision]);

  return null;
}
